import * as d3 from 'd3';

export class ColorScale {

  /**
   * Setup color scale properties
   * @param {Object} props - The properties to initialize the color scale
   * @param {Array} [props.colors] - The array of colors
   * @param {Number} [props.length] - The length of the color scale
   * @param {Object} [props.data] - The data of the chart
   * @param {String} [props.groupBy] - A data attribute to use as grouping criteria
   * @param {Number} [props.offset] - The starting point of the color scale
   * @param {Boolean} [props.reverse] - Reverse the color scale order
   */
  constructor(props) {
    props = {
      ...props
    };
    this.data = props.data || null;
    this.length = props.length || this._dataLength(props) || 15;
    this.groupBy = props.groupBy || null;
    this.offset = props.offset || 0;
    this.reverse = props.reverse || false;
    this.colors = props.colors || this._generateColorMatrix();
  }

  /**
   * Obtain the color scale length from the data property if available
   * @param {object} props - The properties object
   * @return {number} - The length of the color scale or null if unavailable
   */
  _dataLength(props) {
    return props.data ? props.data.series.length : null;
  }

  /**
   * Initialize the color scale
   */
  init() {
    this.colorScale = d3
      .scaleOrdinal()
      .range(this.colors);
  }

  /**
   * Get the color scale
   * @returns {Function}
   */
  get() {
    return this.colorScale;
  }

  /**
   * Set the color scale
   * @param {Function} scale - d3 function for color scale
   */
  set(scale) {
    this.colorScale = scale;
  }

  /**
   * Get the colors array
   * @returns {Array}
   */
  getColors() {
    return this.colors;
  }

  /**
   * Set the colors
   * @param {Array} colors - Array of colors
   */
  setColors(colors) {
    this.colors = colors;
  }

  /**
   * Generate a color matrix
   * @return {array} - An array containing the class names for each color
   */
  _generateColorMatrix() {
    this.groups = this._getGroups();
    this.order = this._getColorOrder();

    const array = [];
    for (let i = 0; i < this.length; i+=1) {
      array.push(`color-data-${this._getColor(i)}`);
    }
    return array;
  }

  /**
   * Obtain an array containing the unique groups based on the groupBy property
   * @return {array} - An array containing the unique groups based on the groupBy property
   */
  _getGroups() {
    return Array.from(new Set(
      Array(this.length)
        .fill(null)
        .map((_, i) =>
          this._getSafe(() => this.data.series[i][this.groupBy]) || i
        )
    ));
  }

  /**
   * Safely retrieve an object attribute regardless of depth
   * @param {function} fn - A function returning the attribute to be returned
   * @param {object} fallback - A fallback value to be returned if the attribute or any of its parent nodes is undefined
   * @return {object} - The attribute to be retrieved, or a fallback value
   */
  _getSafe(fn, fallback) {
    try {
      return fn();
    } catch(_) {
      return fallback;
    }
  }

  /**
   * Obtain an array representing the order of the colors in the color scale
   * @return {array} - An array representing the order of the colors in the color scale
   */
  _getColorOrder() {
    const order = Array(
      this.groups.length
    ).fill(null);

    const repeat = order.map((_, i) => (i%20)+1);
    const result = this._applyOffset(repeat);
    this._applyReverse(result);

    return result;
  }

  /**
   * Apply an offset on an array
   * @param {array} - An array to apply an offset on
   * @return {array} - An array with the offset applied
   */
  _applyOffset(array) {
    const end = array.slice(0, this.offset);
    const start = array.slice(this.offset);
    return start.concat(end);
  }

  /**
   * Reverse an array
   * @param {array} array - An array to reverse
   * @return {array} - The reversed array
   */
  _applyReverse(array) {
    return this.reverse ? array.reverse() : array;
  }

  /**
   * Obtain the color number based on the index of the series
   * @param {number} i - The index of the series
   * @return {number} - The color number corresponding to the series
   */
  _getColor(i) {
    return this._getColorFromData(i) || this._getColorFromOrder(i);
  }

  /**
   * Obtain the color number based on data of the series
   * @param {number} i - The index of the series
   * @return {number} - The color number corresponding to the series or null
   */
  _getColorFromData(i) {
    if (this._getSafe(() => this.data.series[i].color)) {
      return this._getColorFromClass(this.data.series[i].color)
      || this._getColorFromText(this.data.series[i].color)
      || null;
    }
    return null;
  }

  /**
   * Obtain the color number form a valid color class
   * @param {string} colorClass - The color class
   * @return {number} - The color number corresponding to the color class or null
   */
  _getColorFromClass(colorClass) {
    const match = colorClass.match(/color-data-(\d+)$/);
    return match && match[1] > 0 && match[1] <= 20 ? match[1] : null;
  }

  /**
   * Obtain the color number form a valid color definition
   * @param {string} text - A color definition (color alias, color name or hex code)
   * @return {number} - The color number corresponding to the color definition or null
   */
  _getColorFromText(text) {
    const colorNameMatrix = [
      ['categorical 1', 'gray-53', 'gray-42', '#878787', '#6a6a6a'],
      ['categorical 2', 'violet-69', 'violet-80', '#976cf4', '#ba9ef8'],
      ['categorical 3', 'turquoise-37', 'turquoise-66', '#23969a', '#74dcdf'],
      ['categorical 4', 'coral-61', 'tangerine77', '#c86f6d', '#f2af99'],
      ['categorical 5', 'gray-20', 'white', '#333333', '#ffffff'],
      ['categorical 6', 'ocher-44', 'ocher-81', '#b98327', '#fcf5a3'],
      ['categorical 7', 'steel-59', 'steel-71', '#8087ad', '#a6abc6'],
      ['categorical 8', 'pink-64', 'pink-77', '#e75fa0', '#ea9fc5'],
      ['categorical 9', 'violet-36', 'violet-62', '#3e0da9', '#7e4af2'],
      ['categorical 10', 'tangerine-34', 'scarlet-60', '#9b3213', '#e24f72'],
      ['categorical 11', 'slate-59', 'slate-69', '#5b85d0', '#82b6de'],
      ['categorical 12', 'scarlet-17', 'coral-61', '#4d0c2b', '#c86f6d'],
      ['categorical 13', 'pink-49', 'turquoise-30', '#d82177', '#1d797c'],
      ['categorical 14', 'turquoise-14', 'pink-57', '#0c3b35', '#d84b92'],
      ['categorical 15', 'slate-58', 'slate-58', '#7695b0', '#7695b0'],
      ['categorical 16', 'scarlet-26', 'tangerine-64', '#74132a', '#eb7f5d'],
      ['categorical 17', 'steel-31', 'steel-51', '#3e4461', '#68709e'],
      ['categorical 18', 'tangerine-45', 'ocher-65', '#cf4219', '#f9ec51'],
      ['categorical 19', 'ocher-22', 'coral-84', '#5b4013', '#e9c5c4'],
      ['categorical 20', 'scarlet-43', 'scarlet-48', '#bd1f44', '#d2234b']
    ];

    const i = colorNameMatrix.findIndex(colorNames =>
      colorNames.includes(text.toLowerCase())
    );
    return i > -1 ? (i%20) + 1 : null;
  }

  /**
   * Obtain the color number of a series based on the order
   * @param {number} i - The index of the series
   * @return {number} - The color number corresponding to the order
   */
  _getColorFromOrder(i) {
    const index = this._getGroupIndex(i);
    return this.order[index];
  }

  /**
   * Obtain the index of the group to which a series belongs
   * @param {number} i - The index of the series
   * @return {number} - The index of the group to which the series belongs
   */
  _getGroupIndex(i) {
    const group = this._getSafe(() => this.data.series[i][this.groupBy]) || i;
    return this.groups.indexOf(group);
  }

  /**
   * Generate a palette class from the specified string.
   * @param {string} palette – Identifier to convert to a palette class
   */
  static getRangePaletteClass(palette) {
    if (!palette) return 'sequential-4';
    let paletteClass = palette.toLowerCase().split(' ').join('-');

    const paletteMap = {
      'sequential-1': true,
      'sequential-2': true,
      'sequential-3': true,
      'sequential-4': true,
      'sequential-5': true,
      'sequential-6': true,
      'sequential-7': true,
      'sequential-8': true,
      'sequential-9': true,
      'sequential-10': true,
      'diverging-1': true,
      'diverging-2': true,
      'diverging-3': true,
      'diverging-4': true,
      'diverging-5': true,
      'diverging-6': true
    };

    if (paletteMap[paletteClass] !== true) {
      paletteClass = 'sequential-1';
    }

    return paletteClass;
  }
}
